import React, { useMemo, useState } from 'react';
import { ArrowLeft, AlertTriangle, CheckCircle2 } from 'lucide-react';
import './Verification.css';

/**
 * Verification
 * ---------------------------------------------------------------------------
 * Onboarding step that sits AFTER Ingredient Reconciliation and BEFORE
 * Closing Stock. Captures the pre-service "Item Mapping Verification" checks.
 *
 * NOTE: This is a standalone preview screen. It is intentionally NOT wired
 * into App.js / the onboarding flow yet. Local state only, no API calls.
 * Pass onBack / onContinue when you're ready to hook it into the flow.
 *
 * Checks captured:
 *   1. Category & items verification — Chef(s) / Store Manager / Owner
 *      (checkbox per role; all three must be ticked)
 *   2. Raw material weights only — Yes / No
 *   3. Staff food verification (wastage) — Yes / No
 *        → choosing "No" flags it as an owner's deliberate choice
 *   4. Reconciliation check — free-text name of the person in charge
 *   5. Menu entered via — PetPooja / Softcopy
 *        → choosing "Softcopy" warns that item-name checks need >= 3 TSR
 *   6. Recipe / BOM mapping — Yes / No (+ count of items still unmapped)
 *   7. Final sign-off — responsible person's name + auto date/time stamp
 *
 * Interactivity: a live "X of 7 complete" progress bar; the Continue button
 * stays disabled until every required check is answered.
 */

const ROLES = [
  { key: 'chef', label: 'Chef(s)' },
  { key: 'store', label: 'Store Manager' },
  { key: 'owner', label: 'Owner' },
];

const TOTAL_CHECKS = 7;

function Segmented({ options, value, onChange, label }) {
  return (
    <div className="vc-seg" role="group" aria-label={label}>
      {options.map((opt) => (
        <button
          key={opt}
          type="button"
          className={`vc-seg-btn ${value === opt ? 'is-active' : ''}`}
          aria-pressed={value === opt}
          onClick={() => onChange(opt)}
        >
          {opt}
        </button>
      ))}
    </div>
  );
}

function formatStamp(d) {
  if (!d) return '';
  return d.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function Verification({ onBack, onContinue }) {
  // 1. Category & items verification (per role: checked only)
  const [roles, setRoles] = useState({ chef: false, store: false, owner: false });

  // 2–6 answers
  const [rawMaterialWeights, setRawMaterialWeights] = useState(null); // 'Yes' | 'No'
  const [staffFood, setStaffFood] = useState(null); // 'Yes' | 'No'
  const [reconciliationBy, setReconciliationBy] = useState('');
  const [menuSource, setMenuSource] = useState(null); // 'PetPooja' | 'Softcopy'
  const [recipeMapping, setRecipeMapping] = useState(null); // 'Yes' | 'No'
  const [recipeUnmapped, setRecipeUnmapped] = useState('');

  // 7. Final sign-off
  const [signOffName, setSignOffName] = useState('');
  const [signOffAt, setSignOffAt] = useState(null); // Date, stamped on first entry

  const toggleRole = (key) =>
    setRoles((prev) => ({ ...prev, [key]: !prev[key] }));

  const handleSignOffName = (value) => {
    setSignOffName(value);
    if (value.trim() && !signOffAt) setSignOffAt(new Date());
    if (!value.trim()) setSignOffAt(null);
  };

  // ── Completion logic (drives progress + Continue gating) ──────────────
  const status = useMemo(() => {
    const allRolesChecked = ROLES.every((r) => roles[r.key]);
    return {
      categoryItems: allRolesChecked,
      rawMaterialWeights: rawMaterialWeights !== null,
      staffFood: staffFood !== null,
      reconciliation: reconciliationBy.trim().length > 0,
      menuSource: menuSource !== null,
      recipeMapping: recipeMapping !== null,
      signOff: signOffName.trim().length > 0,
    };
  }, [
    roles,
    rawMaterialWeights,
    staffFood,
    reconciliationBy,
    menuSource,
    recipeMapping,
    signOffName,
  ]);

  const completed = useMemo(
    () => Object.values(status).filter(Boolean).length,
    [status]
  );
  const allComplete = completed === TOTAL_CHECKS;
  const pct = Math.round((completed / TOTAL_CHECKS) * 100);

  const payload = useMemo(
    () => ({
      categoryItemsVerification: roles,
      rawMaterialWeightsOnly: rawMaterialWeights,
      staffFoodVerification: staffFood,
      staffFoodOwnerOverride: staffFood === 'No',
      reconciliationCheckBy: reconciliationBy.trim(),
      menuEnteredVia: menuSource,
      softcopyTsrWarning: menuSource === 'Softcopy',
      recipeBomMapped: recipeMapping,
      recipeUnmappedCount:
        recipeMapping === 'No' ? Number(recipeUnmapped) || 0 : 0,
      signOffBy: signOffName.trim(),
      signOffAt: signOffAt ? signOffAt.toISOString() : null,
    }),
    [
      roles,
      rawMaterialWeights,
      staffFood,
      reconciliationBy,
      menuSource,
      recipeMapping,
      recipeUnmapped,
      signOffName,
      signOffAt,
    ]
  );

  const handleContinue = () => {
    if (!allComplete) return;
    if (typeof onContinue === 'function') onContinue(payload);
    // eslint-disable-next-line no-console
    else console.log('Verification check payload:', payload);
  };

  return (
    <div className="vc-page">
      {/* Header */}
      <header className="vc-header">
        <div className="vc-shell vc-header__inner">
          <div className="vc-header-left">
            <button
              type="button"
              className="vc-back-btn"
              aria-label="Back"
              onClick={onBack || undefined}
            >
              <ArrowLeft size={20} />
            </button>
            <h1>Verification Check</h1>
          </div>
          <p className="vc-subtitle">After Reconciliation · before Closing Stock</p>

          <div className="vc-progress">
            <div className="vc-progress-top">
              <span className="vc-progress-label">Verification progress</span>
              <span className={`vc-progress-count ${allComplete ? 'is-done' : ''}`}>
                {completed} of {TOTAL_CHECKS} complete
              </span>
            </div>
            <div
              className="vc-progress-track"
              role="progressbar"
              aria-valuenow={completed}
              aria-valuemin={0}
              aria-valuemax={TOTAL_CHECKS}
            >
              <div
                className={`vc-progress-fill ${allComplete ? 'is-done' : ''}`}
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        </div>
      </header>

      {/* Body */}
      <div className="vc-shell vc-body">
        <div className="vc-section-label">Item Mapping Verification</div>

        {/* 1. Category & items verification */}
        <section className="vc-card">
          <div className="vc-card__title">
            <span className="vc-card__num">1</span>Category &amp; items verification
          </div>
          <p className="vc-card__help">
            Category and items verification done by 3 people. Tick each role that
            has verified.
          </p>
          <div className="vc-role-rows">
            {ROLES.map((r) => (
              <label className="vc-check-label" key={r.key}>
                <input
                  type="checkbox"
                  checked={roles[r.key]}
                  onChange={() => toggleRole(r.key)}
                />
                {r.label}
              </label>
            ))}
          </div>
        </section>

        {/* 2. Raw material weights */}
        <section className="vc-card">
          <div className="vc-card__title">
            <span className="vc-card__num">2</span>Raw material weights
          </div>
          <p className="vc-card__help">
            Verify that they have given the raw material weights only.
          </p>
          <Segmented
            label="Raw material weights only"
            options={['Yes', 'No']}
            value={rawMaterialWeights}
            onChange={setRawMaterialWeights}
          />
        </section>

        {/* 3. Staff food verification */}
        <section className="vc-card">
          <div className="vc-card__title">
            <span className="vc-card__num">3</span>Staff food verification
          </div>
          <p className="vc-card__help">For wastage purpose.</p>
          <Segmented
            label="Staff food verification"
            options={['Yes', 'No']}
            value={staffFood}
            onChange={setStaffFood}
          />
          {staffFood === 'No' && (
            <div className="vc-note" role="note">
              <AlertTriangle size={16} />
              <span>
                Note: Owner has specially selected this option (staff food not
                verified for wastage).
              </span>
            </div>
          )}
        </section>

        {/* 4. Reconciliation check */}
        <section className="vc-card">
          <div className="vc-card__title">
            <span className="vc-card__num">4</span>Reconciliation check
          </div>
          <p className="vc-card__help">Person in charge — who has done this?</p>
          <input
            type="text"
            className="vc-input"
            placeholder="Enter name of person who did the reconciliation"
            value={reconciliationBy}
            onChange={(e) => setReconciliationBy(e.target.value)}
          />
        </section>

        {/* 5. Menu entered via */}
        <section className="vc-card">
          <div className="vc-card__title">
            <span className="vc-card__num">5</span>Menu entered via
          </div>
          <p className="vc-card__help">How was the menu entered?</p>
          <Segmented
            label="Menu source"
            options={['PetPooja', 'Softcopy']}
            value={menuSource}
            onChange={setMenuSource}
          />
          {menuSource === 'Softcopy' && (
            <div className="vc-note vc-note--strong" role="alert">
              <AlertTriangle size={16} />
              <span>Item names check should be done at least 3 TSR for sure.</span>
            </div>
          )}
        </section>

        {/* 6. Recipe / BOM mapping */}
        <section className="vc-card">
          <div className="vc-card__title">
            <span className="vc-card__num">6</span>Roles assigned
          </div>
          <p className="vc-card__help">
            Are the appropriate user roles correctly assigned to the respective staff?
          </p>
          <Segmented
            label="Roles assigned"
            options={['Yes', 'No']}
            value={recipeMapping}
            onChange={setRecipeMapping}
          />
        </section>

        {/* 7. Final sign-off */}
        <section className="vc-card">
          <div className="vc-card__title">
            <span className="vc-card__num">7</span>Final sign-off
          </div>
          <p className="vc-card__help">
            Declaration — the responsible person confirms all checks above are
            true. Date &amp; time are captured automatically.
          </p>
          <input
            type="text"
            className="vc-input"
            placeholder="Enter name of person signing off"
            value={signOffName}
            onChange={(e) => handleSignOffName(e.target.value)}
          />
          {signOffAt && (
            <div className="vc-stamp">
              <CheckCircle2 size={16} color="#157347" />
              <span>
                Signed by <strong>{signOffName.trim()}</strong> ·{' '}
                <strong>{formatStamp(signOffAt)}</strong>
              </span>
            </div>
          )}
        </section>

        {/* Footer actions — equal-width buttons */}
        <div className="vc-footer">
          <button
            type="button"
            className="vc-btn vc-btn--secondary"
            onClick={onBack || undefined}
          >
            Back
          </button>
          <button
            type="button"
            className="vc-btn vc-btn--primary"
            onClick={handleContinue}
            disabled={!allComplete}
            title={
              allComplete
                ? 'Proceed to Closing Stock'
                : `Complete all ${TOTAL_CHECKS} checks to continue`
            }
          >
            Continue
          </button>
        </div>
        {!allComplete && (
          <p className="vc-hint">
            {TOTAL_CHECKS - completed} check
            {TOTAL_CHECKS - completed === 1 ? '' : 's'} left before you can
            continue.
          </p>
        )}
      </div>
    </div>
  );
}

export default Verification;
